import React from "react";
import { Alert, Container, Spinner } from "reactstrap";

import { BetHistoryPeriod, getMyBetHistory } from "../../../helpers/api/bet";
import { useAuthStore } from "../../../store/useAuthStore";
import type { UserBetHistoryItem } from "../../../types/api.generated";
import AvatarEditorModal from "./AvatarEditorModal";

type ProfilePeriod = "weekly" | "monthly" | "all";

interface BalancePoint {
  date: string;
  value: number;
}

const periodOptions: Array<{ key: ProfilePeriod; label: string }> = [
  { key: "weekly", label: "7D" },
  { key: "monthly", label: "30D" },
  { key: "all", label: "All" },
];

export const formatProfileNumber = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return new Intl.NumberFormat("en-US").format(Math.round(value));
};

export const formatProfileSignedNumber = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${formatProfileNumber(value)}`;
};

export const formatProfilePercent = (value: number) => `${Math.round(Math.max(0, Math.min(100, value)))}%`;

export const formatProfileDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const shortenProfileWallet = (wallet?: string | null) => {
  if (!wallet) return "-";
  if (wallet.length <= 12) return wallet;
  return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
};

export const getProfileName = (user?: { username?: string | null; walletAddress?: string | null; address?: string | null } | null) => {
  return user?.username || shortenProfileWallet(user?.walletAddress || user?.address) || "Champion";
};

export const getProfileInitial = (name?: string | null) => {
  const trimmed = name?.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
};

export const isProfileWinningBet = (bet: UserBetHistoryItem) => {
  if (["won", "paid"].includes(bet.status)) return true;
  if (["lost", "refunded"].includes(bet.status)) return false;
  return Number(bet.netProfit) > 0;
};

export const buildProfileStats = (bets: UserBetHistoryItem[]) => {
  const wins = bets.filter(isProfileWinningBet).length;
  const losses = Math.max(0, bets.length - wins);
  const bestBet = bets.reduce<UserBetHistoryItem | undefined>((best, bet) => {
    if (!best) return bet;
    return Number(bet.netProfit) > Number(best.netProfit) ? bet : best;
  }, undefined);

  return {
    betsPlaced: bets.length,
    wins,
    losses,
    winRate: bets.length === 0 ? 0 : (wins / bets.length) * 100,
    profitTotal: bets.reduce((total, bet) => total + Number(bet.netProfit || 0), 0),
    bestGain: bestBet ? Number(bestBet.netProfit || 0) : 0,
    bestBetId: bestBet?.battleId ?? bestBet?.id,
    openStake: bets
      .filter((bet) => ["placed", "locked", "waiting_result"].includes(bet.status))
      .reduce((total, bet) => total + Number(bet.stakedPoints || 0), 0),
  };
};

export const buildProfileBalancePoints = (bets: UserBetHistoryItem[], balance: number): BalancePoint[] => {
  const ordered = bets.slice().sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
  const totalNet = ordered.reduce((total, bet) => total + Number(bet.netProfit || 0), 0);
  let running = balance - totalNet;

  return ordered.map((bet) => {
    running += Number(bet.netProfit || 0);
    return { date: bet.createdAt, value: running };
  });
};

export const getProfileSelectedCharacterName = (bet: UserBetHistoryItem) => bet.selectedCharacter?.name || "-";

export const getProfileBattleTitle = (bet: UserBetHistoryItem) => bet.battleTitle || "Battle";

const StatCard = ({ label, value, detail, tone }: { label: string; value: string; detail: string; tone?: "positive" | "negative" }) => (
  <article className="profile-stat">
    <span>{label}</span>
    <strong className={tone === "positive" ? "is-positive" : tone === "negative" ? "is-negative" : undefined}>{value}</strong>
    <small>{detail}</small>
  </article>
);

const ProfileBalanceChart = ({ points, balance }: { points: BalancePoint[]; balance: number }) => {
  const safePoints = points.length > 0 ? points : [{ date: "", value: balance }];
  const values = safePoints.map((point) => Number(point.value));
  const min = Math.min(...values, 0);
  const max = Math.max(...values, balance || 1);
  const range = max - min || 1;
  const width = 720;
  const height = 170;
  const xStep = values.length > 1 ? width / (values.length - 1) : width;
  const coordinates = values.map((value, index) => {
    const x = index * xStep;
    const y = height - ((value - min) / range) * (height - 24) - 12;
    return `${x},${y}`;
  });
  const area = `0,${height} ${coordinates.join(" ")} ${width},${height}`;

  return (
    <div className="profile-balance-chart__frame" data-testid="profile-balance-chart">
      <div className="profile-balance-chart__summary">
        <span>Balance</span>
        <strong>{formatProfileNumber(balance)} PTS</strong>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Balance evolution">
        <defs>
          <linearGradient id="profile-balance-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(0, 255, 255, 0.5)" />
            <stop offset="100%" stopColor="rgba(0, 255, 255, 0)" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#profile-balance-area)" />
        <polyline points={coordinates.join(" ")} fill="none" stroke="var(--c-cyan)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const Profile = () => {
  document.title = "Legends | The Colosseum - Profile";

  const user = useAuthStore((state) => state.user);
  const [period, setPeriod] = React.useState<ProfilePeriod>("all");
  const [bets, setBets] = React.useState<UserBetHistoryItem[]>([]);
  const [total, setTotal] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [isEditingAvatar, setIsEditingAvatar] = React.useState(false);
  const setUser = useAuthStore((state) => state.setUser);

  React.useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getMyBetHistory({ period: period as BetHistoryPeriod });
        if (!active) return;
        setBets(response.items);
        setTotal(response.meta.totalItems);
      } catch (e: any) {
        if (active) setError(typeof e === "string" ? e : e?.message || "Failed to load profile history");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      active = false;
    };
  }, [period]);

  const wallet = user?.walletAddress || user?.address || "";
  const profileName = getProfileName(user);
  const balance = user?.pointsBalance ?? 0;
  const stats = React.useMemo(() => buildProfileStats(bets), [bets]);
  const chartPoints = React.useMemo(() => buildProfileBalancePoints(bets, balance), [bets, balance]);
  const weeklyNet = React.useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return bets
      .filter((bet) => {
        const time = new Date(bet.createdAt).getTime();
        return !Number.isNaN(time) && time >= sevenDaysAgo;
      })
      .reduce((sum, bet) => sum + Number(bet.netProfit || 0), 0);
  }, [bets]);
  const displayedTotal = total ?? stats.betsPlaced;

  const copyWallet = async () => {
    if (!wallet) return;
    await navigator.clipboard?.writeText(wallet);
    setCopied(true);
  };

  return (
    <div className="page-content profile-page" data-cy="profile-page">
      <Container fluid>
        <div className="profile-layout">
          <aside className="profile-sidebar" aria-label="Player information">
            <section className="profile-identity-card">
              {user?.avatar ? (
                <img className="profile-avatar profile-avatar--image" src={user.avatar} alt={`${profileName} avatar`} data-cy="profile-avatar-image" />
              ) : (
                <span className="profile-avatar" aria-hidden="true">{getProfileInitial(profileName)}</span>
              )}
              <h1>{profileName}</h1>
              <p>{shortenProfileWallet(wallet)}</p>
              <div className="profile-actions">
                <button type="button" onClick={copyWallet} disabled={!wallet} data-cy="profile-copy-wallet">
                  <i className="ri-file-copy-line" aria-hidden="true" /> {copied ? "Copied" : "Copy"}
                </button>
                <button type="button" onClick={() => setIsEditingAvatar(true)} data-cy="profile-edit-avatar">
                  <i className="ri-edit-line" aria-hidden="true" /> Edit
                </button>
              </div>
            </section>

            <section className="profile-balance-card">
              <span>Balance</span>
              <strong>{formatProfileNumber(balance)}</strong>
              <small>PTS</small>
              <dl>
                <div>
                  <dt>Open stake</dt>
                  <dd>{formatProfileNumber(stats.openStake)} PTS</dd>
                </div>
                <div>
                  <dt>7-day net</dt>
                  <dd className={weeklyNet >= 0 ? "is-positive" : "is-negative"}>{formatProfileSignedNumber(weeklyNet)} PTS</dd>
                </div>
              </dl>
            </section>

            <section className="profile-account-card">
              <h2>Account</h2>
              <dl>
                <div>
                  <dt>Email</dt>
                  <dd>{user?.email || "-"}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>{user?.role === "admin" ? "Admin" : "Player"}</dd>
                </div>
                <div>
                  <dt>Joined</dt>
                  <dd>{formatProfileDate(user?.createdAt)}</dd>
                </div>
              </dl>
            </section>
          </aside>

          <main className="profile-main">
            <section className="profile-stats" aria-label="Profile betting statistics">
              <StatCard label="Bets placed" value={formatProfileNumber(stats.betsPlaced)} detail={`${formatProfileNumber(displayedTotal)} total bets`} />
              <StatCard label="Win rate" value={formatProfilePercent(stats.winRate)} detail={`${formatProfileNumber(stats.wins)} W · ${formatProfileNumber(stats.losses)} L`} />
              <StatCard label="Total profit" value={`${formatProfileSignedNumber(stats.profitTotal)} PTS`} detail="All periods" tone={stats.profitTotal >= 0 ? "positive" : "negative"} />
              <StatCard label="Best bet" value={`${formatProfileNumber(stats.bestGain)} PTS`} detail={`#${stats.bestBetId ?? "-"}`} tone={stats.bestGain >= 0 ? "positive" : "negative"} />
            </section>

            <section className="profile-balance-chart">
              <div className="profile-period-tabs" role="tablist" aria-label="Profile period">
                {periodOptions.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    className={period === option.key ? "is-active" : ""}
                    onClick={() => setPeriod(option.key)}
                    data-cy={`profile-period-${option.key}`}
                    role="tab"
                    aria-selected={period === option.key}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <ProfileBalanceChart points={chartPoints} balance={balance} />
            </section>

            <section className="profile-history-card">
              <header className="profile-history-card__header">
                <h2>History</h2>
                <span>{formatProfileNumber(displayedTotal)} bets</span>
              </header>

              {error && <Alert color="danger" role="alert">{error}</Alert>}

              <div className="profile-history-wrap">
                <table className="profile-history-table">
                  <thead>
                    <tr>
                      <th>Battle</th>
                      <th>Selected character</th>
                      <th>Stake</th>
                      <th>Payout</th>
                      <th>Net</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bets.map((bet, index) => {
                      const net = Number(bet.netProfit || 0);
                      return (
                        <tr key={bet.id || `${bet.battleId}-${index}`} data-cy="profile-history-row" data-testid="profile-history-row">
                          <td className="profile-battle-cell">
                            <strong>{getProfileBattleTitle(bet)}</strong>
                          </td>
                          <td className="profile-selected-character">{getProfileSelectedCharacterName(bet)}</td>
                          <td>{formatProfileNumber(bet.stakedPoints)}</td>
                          <td>{formatProfileNumber(bet.payoutPoints)}</td>
                          <td className={net >= 0 ? "is-positive" : "is-negative"}>{formatProfileSignedNumber(net)}</td>
                          <td>
                            <span className={`profile-status ${isProfileWinningBet(bet) ? "profile-status--won" : "profile-status--lost"}`}>
                              {isProfileWinningBet(bet) ? "Won" : "Lost"}
                            </span>
                          </td>
                          <td>{formatProfileDate(bet.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {loading && (
                <div className="profile-state" data-cy="profile-loading">
                  <Spinner size="sm" /> Loading profile...
                </div>
              )}

              {!loading && bets.length === 0 && !error && (
                <div className="profile-state" data-cy="profile-empty" data-testid="profile-empty">No bets found</div>
              )}
            </section>
          </main>
        </div>
      </Container>
      {isEditingAvatar && (
        <AvatarEditorModal
          onClose={() => setIsEditingAvatar(false)}
          onSaved={(avatar) => {
            if (user) setUser({ ...user, avatar });
            setIsEditingAvatar(false);
          }}
        />
      )}
    </div>
  );
};

export default Profile;
